<template lang="pug">
.stream-link-list(v-if="streams.length > 1" :class="[mode]")
  Modal.loading-modal(v-if="showIsLoading" @close="showIsLoading = null" :header="showIsLoading.header")
    p {{showIsLoading.title}}
    p {{showIsLoading.body}}
    RogueButton(@click="showIsLoading = null" width="full" ) Close

  RogueButton.stream-btn.desktop(
    color="white"
    v-for="stream in webStreams"
    :key="stream.name"
    :class="{'active' : isSplit(stream.name)}"
    @click="toggleSplit(stream)"
    text-align="text-left"
  ) {{ stream.name }}

    Icon.left-icon(
      v-if="stream.type"
      :name="getStreamIcon(stream)"
      :fill="isSplit(stream.name) ? 'black' : 'white'"
    )
    Icon.lock-icon(
      v-if="stream.paid"
      :name="lockIconName"
      :fill="isSplit(stream.name) ? 'black' : 'white'"
    )

  button.player-btn.desktop(
    v-if="showMultiview"
    type="button"
    :class="{active: multiviewDrawerOpen}"
    :aria-label="__('Stream Viewer', eventNamespace)"
    :aria-expanded="multiviewDrawerOpen"
    :title="__('Stream Viewer', eventNamespace)"
    @click="emit('toggleMultiview')"
  )
    Icon.multiview-icon(name="media/multiview" :fill="multiviewDrawerOpen ? 'black' : 'white'")
  button.player-btn.desktop(
    v-if="showFullscreen"
    type="button"
    :aria-label="__('Fullscreen', eventNamespace)"
    :title="__('Fullscreen', eventNamespace)"
    @click="emit('toggleFullscreen')"
  )
    Icon.fullscreen-icon(name="media/fullscreen" fill="white")

  .streams-switcher-mobile-container
    .streams-switcher(ref="streamsSwitcher")
      button(type="button" :aria-expanded="showStreamsMenu" @click.prevent="showStreamsMenu = !showStreamsMenu")
        .streams-switcher-box
          Icon.left-icon(
            v-if="getStreamIcon(selectedStream)"
            :name="getStreamIcon(selectedStream)"
            fill="black"
          )
          Icon.lock-icon(v-if="selectedStream?.paid" :name="lockIconName" fill="black")

          | {{selectedStream?.name || 'Live Stream'}}

        Icon.icon-size-xs(fill="black" :name="showStreamsMenu ? 'triangle-up' : 'triangle-down'")

      .divisions-menu(v-show="showStreamsMenu")
        button.division-switch-item(
          v-for="stream in webStreams"
          v-show="stream.name !== selectedStream?.name"
          :key="stream.name"
          type="button"
          @click="toggleSplit(stream)"
        )
          Icon.left-icon(
            v-if="stream.type"
            :name="getStreamIcon(stream)"
            fill="black"
          )
          | {{ stream.name }}

    button.player-btn(
      v-if="showMultiview"
      type="button"
      :class="{active: multiviewDrawerOpen}"
      :aria-label="__('Stream Viewer', eventNamespace)"
      :aria-expanded="multiviewDrawerOpen"
      :title="__('Stream Viewer', eventNamespace)"
      @click="emit('toggleMultiview')"
    )
      Icon.multiview-icon(name="media/multiview" :fill="multiviewDrawerOpen ? 'black' : 'white'")
    button.player-btn(
      v-if="showFullscreen"
      type="button"
      :aria-label="__('Fullscreen', eventNamespace)"
      :title="__('Fullscreen', eventNamespace)"
      @click="emit('toggleFullscreen')"
    )
      Icon.fullscreen-icon(name="media/fullscreen" fill="white")

  RogueButton.ybvr-btn(
    v-for="stream in ybvrNativeStreams"
    :key="stream.name"
    @click="toggleSplit(stream)"
    :color="mode === 'dark' ? 'white-outline' : 'black'"
    width="full"
  )
    Icon(name="media/vr-mobile-icon" fill="white")
    span {{ stream.name }}
  RogueButton.ybvr-btn(v-if="showControlRoomButton" @click="showVrModal = true" :color="mode === 'dark' ? 'white-outline' : 'black'" width="full")
    Icon(name="media/vr-mobile-icon" fill="white")
    span {{ __('Watch in VR Control Room', eventNamespace) }}
  p.loading-text(v-if="showIsLoading") {{showIsLoading.title}}
  Modal.vr-modal(v-if="showVrModal" title="Watch In VR Control Room" @close="showVrModal = false")
    h2 {{ __('Watch In VR Control Room', eventNamespace) }}
    p {{__('Proceed to the Rogue App on your mobile device to start watching events in our immersive VR Control Room.')}}
    p
      b {{__('Note: ')}}
      i {{__('VR Control room is available exclusively to users who purchase Iron Game Premium.')}}
    RogueButton(color="black" width="full" :link="appStoreLink" text="Continue to Rogue App")
</template>
<script setup lang="ts">
import { ref, computed, inject, nextTick, onBeforeUnmount, watch } from 'vue'
import { eventNamespace } from '../../js/constants'
import { useRogue } from '../../js/rogue'

type LoadingMessage = { header: string; title: string; body: string }

interface Stream {
  name: string
  type?: string
  paid?: boolean
  active?: boolean
  videoSrcUrl?: string
  forceM3U8?: string | false
  forceMPD?: string | false
  ybvrDisabled?: LoadingMessage
  controls?: { playing: boolean; volume: number }
  _data?: Stream
}

const props = withDefaults(defineProps<{
  activeStream: Record<string, any>
  splitNames: string[]
  hasTicket?: boolean
  mode?: 'dark' | 'light'
  ybvrSupported?: boolean
  showMultiview?: boolean
  multiviewDrawerOpen?: boolean
  showFullscreen?: boolean
}>(), {
  hasTicket           : false,
  mode                : 'dark',
  ybvrSupported       : false,
  showMultiview       : false,
  multiviewDrawerOpen : false,
  showFullscreen      : false,
})

const emit = defineEmits<{
(e: 'setSplit', streamNames: string[]): void
(e: 'toggleMultiview'): void
(e: 'toggleFullscreen'): void
}>()

const APP_URLS = {
  iosAppStore : 'https://itunes.apple.com/us/app/rogue-app/id1106322870?ls=1&mt=8',
  googlePlay  : 'https://play.google.com/store/apps/details?id=com.roguefitness.app',
}

// Keys are lowercase; stream names are matched case-insensitively
const STREAM_ICONS_BY_NAME: Record<string, string> = {
  'wide cam'          : 'media/wide-cam',
  'cable cam'         : 'media/cable-cam',
  'leader cam'        : 'media/leader-cam',
  'ceiling cam'       : 'media/ceiling-cam',
  'high end zone cam' : 'media/high-end-zone-cam',
  'low end zone cam'  : 'media/low-end-zone-cam',
  'overhead cam'      : 'media/overhead-cam',
}

const STREAM_ICONS_BY_TYPE: Record<string, string> = {
  YouTube    : 'media/free-stream',
  Cloudflare : 'media/iron-game',
  YBVR       : 'media/vr-black',
}

const showOverlay = inject<(name: string) => void>('showOverlay', () => {})
const rogue = useRogue()

const showVrModal = ref(false)
const showIsLoading = ref<LoadingMessage | null>(null)
const showStreamsMenu = ref(false)
const streamsSwitcher = ref<HTMLElement | null>(null)

// HlsVideo also writes to this debug trail, so it must exist once the stream list is set up
if (typeof window !== 'undefined') {
  (window as any).mmvLog = (window as any).mmvLog || []
}

const logMmv = (message: string) => {
  if (typeof window !== 'undefined') (window as any).mmvLog.push(message)
}

const streams = computed<Stream[]>(() => {
  const allLinks: Stream[] = props.activeStream?.streamLinks || []

  if (rogue.appContext?.platform === 'ios') {
    return allLinks.filter(l => l.forceM3U8 !== false && l._data?.forceM3U8 !== false)
  }

  return allLinks
})

const split = computed(() => {
  return props.splitNames.map(name => streams.value.find(s => s.name === name)).filter(Boolean) as Stream[]
})

// The mobile switcher shows the primary stream of the current split
const selectedStream = computed<Stream | undefined>(() => split.value[0] || streams.value[0])

const webStreams = computed(() => {
  return streams.value.filter(s =>
    s.type !== 'YBVR' || (props.ybvrSupported && !rogue.appContext)
  )
})

const ybvrNativeStreams = computed(() => {
  return streams.value.filter(s =>
    s.type === 'YBVR' && rogue.appContext && s.active
  )
})

const isIOS = computed(() => {
  if (typeof window === 'undefined' || !window?.navigator) return false

  const platform = window.navigator.platform || ''
  const isIosPlatform = [
    'iPad Simulator', 'iPhone Simulator', 'iPod Simulator',
    'iPad', 'iPhone', 'iPod',
  ].includes(platform)

  const isModerniPad = window.navigator.userAgent.includes("Mac") && ("ontouchend" in document)

  return isIosPlatform || isModerniPad
})

const appStoreLink = computed(() => {
  return isIOS.value ? APP_URLS.iosAppStore : APP_URLS.googlePlay
})

const showControlRoomButton = computed(() => {
  // Show app deep-link button if browser doesn't support YBVR but streams are active
  return !props.ybvrSupported && streams.value.some(s => s.type === 'YBVR' && s.active)
})

const lockIconName = computed(() => props.hasTicket ? 'media/unlock' : 'media/lock')

const isSplit = (name: string) => props.splitNames[0] === name

const getStreamIcon = (stream?: Partial<Stream> | null) => {
  const { type, name } = stream || {}

  return STREAM_ICONS_BY_NAME[name?.toLowerCase() ?? ''] || STREAM_ICONS_BY_TYPE[type ?? ''] || null
}

const toggleSplit = (stream: Stream) => {
  if (!props.hasTicket && stream.paid) {
    showOverlay('buyPremium')

    return
  }

  if (stream?.type === 'YBVR' && rogue.appContext && rogue.emitToApp) {
    const { platform } = rogue.appContext

    // Cross-platform manifest switching
    let videoUrl: string | false | undefined = platform === 'ios'
      ? stream.videoSrcUrl?.replace('.mpd', '.m3u8')
      : stream.videoSrcUrl?.replace('.m3u8', '.mpd')

    const playerType = platform === 'ios' ? 'rn' : 'native'
    const streamData = stream._data || stream

    // Override URLs if specifically forced
    if (playerType === 'rn' && streamData?.forceM3U8 !== undefined) {
      videoUrl = streamData.forceM3U8
    }
    if (playerType === 'native' && streamData?.forceMPD !== undefined) {
      videoUrl = streamData.forceMPD
    }

    if (videoUrl === false) {
      showIsLoading.value = streamData.ybvrDisabled || {
        header : 'Control Room',
        title  : 'Control Room stream is processing.',
        body   : 'Please check back later.',
      }
    } else {
      rogue.emitToApp('showYBVRPlayer', { videoUrl, title: stream.name, playerType })
      showOverlay('ybvrNative')
    }
  } else {
    // Normal Web Stream switching
    emit('setSplit', [stream?.name])
  }

  if (stream?.type !== 'YBVR' || !rogue.appContext) {
    updatePlayStatus()
    logMmv(`split: ${split.value.map(s => s?.name).join(',')}`)
  }
  showStreamsMenu.value = false
}

const force = () => {
  // Find the best stream candidate to display
  // Priority: Already playing > YouTube fallback > First stream in list
  const stream = streams.value.find(s => s.controls?.playing) ||
                 streams.value.find(s => s.type === 'YouTube') ||
                 streams.value[0]

  if (stream) {
    if (stream.controls) {
      stream.controls.playing = true
    }

    emit('setSplit', [stream.name])
    updatePlayStatus()
    logMmv(`force ${stream.name}`)
  }
}

const updatePlayStatus = () => {
  nextTick(() => {
    // The stream that keeps its volume (or the primary) stays audible; the rest are muted
    const audible = split.value.find(s => s?.controls?.volume) || split.value[0]
    // Visible streams play only if one of them is already playing
    const anyPlaying = split.value.some(s => s?.controls?.playing)

    streams.value.forEach(s => {
      if (!s.controls) return

      s.controls.volume = s === audible ? 100 : 0
      s.controls.playing = anyPlaying && props.splitNames.includes(s.name)
    })
  })
}

const handleClickOutside = (event: MouseEvent) => {
  const switcher = streamsSwitcher.value
  if (switcher && !switcher.contains(event.target as Node)) {
    showStreamsMenu.value = false
  }
}

// Only listen for outside clicks while the mobile menu is open
watch(showStreamsMenu, (open) => {
  if (open) {
    document.addEventListener('click', handleClickOutside)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

watch(() => props.activeStream?.id, (newVal) => {
  if (newVal) {
    force()
  }
}, { immediate: true })

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

</script>
<style lang="scss" scoped>
.stream-link-list {
  display: flex;
  background: $color-black;
  padding: $space-xs;
  width: 100%;
  align-items: stretch;
  justify-content: flex-start;
  flex-direction: row;
  gap: $space-xs;
  overflow-x: auto;

  @include not-desktop() {
    flex-wrap: wrap;
    overflow: unset;
  }

  @include all-desktop() {
    justify-content: space-between;
  }

  Button.stream-btn {
    max-width: 33%;
    width: 100%;
    min-width: fit-content;
    padding: $size-xs;
    font-size: $size-xs;
    color: $color-white;
    border: 2px solid $color-white;
    background-color: $color-black;
    text-transform: uppercase;
    font-weight: $weight-bold;

    // the native apps were adding margin bottoms that were impacting layout
    margin-bottom: 0 !important;

    &:hover {
      color: $color-white;
      background-color: $color-black-light;
    }

    &.active {
      color: $color-black;
      background-color: $color-white;

      &:hover {
        color: $color-black-light;
      }
    }

    @include all-desktop() {
      padding: 9px;

      .text-left{
        text-align: center;
      }
    }

    @include large-xl {
      padding: 10px 8px 10px 53px;
      font-size: $size-xxs;

      > span{
        margin-left: 25%;
      }
    }

    @include tablet-and-small-desktop {
      > span{
        margin-left: 25%;
      }
    }

    @include tablet {
      padding: $size-s $size-s $size-s 88px;
    }

    @include not-desktop() {
      max-width: 100%;
      width: calc(50% - 6px);

      &:last-of-type.stream-btn:nth-child(odd) {
        min-width: 100%;
        width: 100%;
      }
    }

    @include mobile() {
      font-size: $size-xxs;

      .icon {
        top: $size-xxs;
      }

      &:last-of-type.stream-btn:nth-child(odd) {
        padding-left: 78px; //14px*2 + img width

        .left-icon {
          left: $size-xs;
          top: 7px;
          width: 50px;
          height: 28px;
          display: flex;
        }
      }
    }

    .icon {
      position: absolute;
      right: 10px;
      top: 9px;
    }

    span {
      position: relative;

      .slot {
        text-align: left !important;
      }
    }

    .left-icon {
      position: absolute;
      left: 6px;
      top: 6px;
      width: 40px;
      height: 22px;
      display: none;

      @include large-xl {
        display: flex;
      }

      @include tablet {
        display: flex;
        width: 68px;
        height: 38px;
      }
    }
  }

  .stream-btn.desktop{
    @include not-desktop {
        display: none;
      }
  }

  .player-btn {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    padding: 0;
    border: 2px solid $color-white;
    border-radius: var(--btn-border-radius, 3px);
    background-color: $color-black;
    cursor: pointer;

    &:hover {
      background-color: $color-black-light;
    }

    &.active,
    &.active:hover {
      background-color: $color-white;
    }

    .multiview-icon {
      width: 24px;
      height: $size-s;
    }

    .fullscreen-icon {
      width: $size-s;
      height: $size-s;
    }

    // Pinned to the far right of the bar regardless of DOM order
    &.desktop {
      order: 1;

      @include not-desktop {
        display: none;
      }
    }
  }

  Button.ybvr-btn {
    .icon {
      display: inline-block;
      height: 20px;
      margin-right: 8px;
      margin-top: -5px;
      width: 20px;
      position: relative;
      top: 4px;
    }
  }

  &.light {
    background-color: $color-gray-x-light;

    Button.stream-btn {
      border: 1px solid $color-black;

      &:hover {
        border-color: $color-black-light;
      }
    }
  }
}

.vr-modal {

  p,
  h2 {
    text-align: center;
  }
}

.streams-switcher-mobile-container {
  width: 100%;
  position: absolute;
  text-align: left;
  margin-bottom: 5px;
  top: 0;
  left: 0;
  padding: $size-xxs $size-xxs;
  background-color: $color-black;
  display: flex;
  align-items: stretch;
  gap: $space-xxs;

  @include all-desktop {
    display: none;
  }
}

.streams-switcher {
    flex-grow: 1;
    min-width: 0;
    text-align: left;

    > button {
      font-family: RobotoCondensedBold;
      text-transform: uppercase;
      letter-spacing: .05em;
      font-size: 18px;
      line-height: 18px;
      padding: 8px 18px 8px 8px;
      width: 100%;
      margin: 0 auto;
      border-radius: 0.375rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-style: solid;
      background-color: $color-white;
      border-color: $color-black;
      color: $color-black;
      &:focus,
      &:active,
      &:hover {
          cursor: pointer;
      }
      @media (max-width: 400px) {
        padding: 6px 14px 6px 6px;
      }
    }

    .divisions-menu {
        position: absolute;
        background-color: $color-white;
        top: 57px;
        z-index: 1000;
        min-width: 200px;
        min-height: 85px;
        border-radius: 3px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
        max-height: 300px;
        overflow: auto;
        left: 14px;
        right: 14px;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        @media (max-width: 400px) {
          top: 46.5px;
          font-size: $size-xs;
        }
        
        .division-switch-item {
          display: flex;
          align-items: center;
          width: 100%;
          border: 0;
          background: none;
          text-align: left;
          line-height: inherit;
          font-family: RobotoCondensedBold;
          font-size: 18px;
          cursor: pointer;
          padding: 10px 15px 10px 10px;
          color: $color-black;
          &:hover {
              background-color: $color-gray-x-light;
          }

          @media (max-width: 400px) {
            font-size: $size-xs;
          }
           span{
            margin-right: $size-xxs;
           }
        }
    }

    .left-icon{
      width: 54px;
      height: 30px;

      @media (max-width: 400px) {
        width: 40px;
        height: 22px;
      }
    }

    @supports (-webkit-overflow-scrolling: touch) {
    > button {
      font-size: 16px;
      padding-top: 11px;
      padding-bottom: 11px;
    }
  }
}

.streams-switcher-box{
  width: 80%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  span{
    margin-right: $size-s;
    width: 40px;
    height: 22px;
  }

  @media (max-width: 400px) {
    font-size: $size-xs;
  }
}

</style>
